import { forwardRef } from 'react'

import { tv, VariantProps } from 'tailwind-variants'

import { Icon } from '@/components/V2/atoms'

export interface TLabelTagProps {
  type?: 'debit' | 'credit' | 'default' | 'warning' | 'disabled'
  text?: string
  icon?: React.ReactNode
  iconPosition?: 'left' | 'right'
}

export const labelTagVariants = tv({
  slots: {
    containerSlot:
      'flex h-[19px] w-[62px] flex-row items-center justify-center gap-2 rounded-2xl border px-1 py-0.5',
    textSlot:
      'overflow-hidden text-ellipsis whitespace-nowrap text-[8px] font-bold uppercase leading-[150%]'
  },
  variants: {
    type: {
      credit: {
        containerSlot:
          'bg-surface-white-500 border-surface-white-300 min-w-[67px] max-w-[67px]',
        textSlot: 'text-surface-white-400'
      },
      debit: {
        containerSlot:
          'bg-surface-white-500 border-surface-white-300 min-w-[67px] max-w-[67px]',
        textSlot: 'text-surface-white-400'
      },
      default: {
        containerSlot:
          'bg-primary-400 border-primary-400 min-w-[67px] max-w-[67px]',
        textSlot: 'text-surface-white-50'
      },
      warning: {
        containerSlot:
          'bg-danger-50 border-danger-100 !w-auto gap-1 border-[0.7px]',
        textSlot: 'text-danger-200 overflow-visible text-clip text-[10px]'
      },
      disabled: {
        containerSlot:
          'bg-danger-50 border-danger-100 min-w-[67px] max-w-[67px] gap-1',
        textSlot: 'text-danger-200'
      }
    }
  },
  defaultVariants: {
    type: 'default'
  }
})

export type TLabelTagVariants = VariantProps<typeof labelTagVariants>

/**
 * @deprecated Use `Badge` from `@alternative/acqua/atoms` instead. acqua has no dedicated tag component; Badge covers the same colored-label use case.
 */
export const LabelTag = forwardRef<HTMLDivElement, TLabelTagProps>(
  (
    { type = 'default', text: textProp, icon: iconProp, iconPosition = 'left' },
    ref
  ) => {
    const { containerSlot, textSlot } = labelTagVariants({ type })

    const textType = {
      debit: 'DEBIT',
      credit: 'CREDIT',
      default: 'DEFAULT',
      warning: 'EXPIRED',
      disabled: 'DISABLED'
    }[type]
    const text = textProp || textType
    const icon =
      iconProp ||
      (type === 'warning' ? (
        <Icon name='error' color='danger' size='xs' />
      ) : undefined)

    return (
      <div className={containerSlot()} ref={ref}>
        {iconPosition === 'left' && icon}
        <div className={textSlot()}>{text}</div>
        {iconPosition === 'right' && icon}
      </div>
    )
  }
)

LabelTag.displayName = 'LabelTag'
